import { useState, useEffect } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Copy,
  Play,
  RefreshCw,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Spinner,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Textarea,
  useToast,
} from '@centraoneplus/ui';
import {
  useExecuteCapability,
  useTestCapability,
  useMcpSchema,
  useOpenAISchema,
} from '@hooks/useIntegrations';
import type { Capability, ExecutionResult } from '@services/integrations.service';

interface TestingConsoleProps {
  capability: Capability;
  onClose: () => void;
  // Set when opened from a ticket's "Run capability" entry point, so the
  // execution is attributed to that ticket in the audit trail.
  ticketId?: string;
}

export function TestingConsole({ capability, onClose, ticketId }: TestingConsoleProps) {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('result');
  const [paramValues, setParamValues] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<ExecutionResult | null>(null);

  // Hooks
  const executeCapability = useExecuteCapability();
  const testCapability = useTestCapability();
  const { data: mcpSchema, isLoading: mcpLoading } = useMcpSchema(capability.id);
  const { data: openaiSchema, isLoading: openaiLoading } = useOpenAISchema(capability.id);

  // Initialize parameter values with defaults
  useEffect(() => {
    const defaults: Record<string, unknown> = {};
    capability.parameters.forEach((param) => {
      if (param.default !== undefined) {
        defaults[param.name] = param.default;
      } else if (param.type === 'boolean') {
        defaults[param.name] = false;
      } else if (param.type === 'integer' || param.type === 'number') {
        defaults[param.name] = '';
      } else {
        defaults[param.name] = '';
      }
    });
    setParamValues(defaults);
  }, [capability]);

  const handleParamChange = (name: string, value: unknown) => {
    setParamValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleExecute = async () => {
    // Convert values to proper types
    const params: Record<string, unknown> = {};
    capability.parameters.forEach((param) => {
      const value = paramValues[param.name];
      if (value === '' || value === undefined) {
        return; // Skip empty values
      }

      switch (param.type) {
        case 'integer':
          params[param.name] = parseInt(value as string, 10);
          break;
        case 'number':
          params[param.name] = parseFloat(value as string);
          break;
        case 'boolean':
          params[param.name] = value;
          break;
        case 'array':
          params[param.name] = typeof value === 'string'
            ? value.split(',').map((v) => v.trim())
            : value;
          break;
        default:
          params[param.name] = value;
      }
    });

    try {
      const res = await executeCapability.mutateAsync({
        capabilityId: capability.id,
        data: { parameters: params, ...(ticketId ? { ticketId } : {}) },
      });
      setResult(res);
    } catch (err) {
      setResult({
        success: false,
        error: err instanceof Error ? err.message : 'Execution failed',
      });
    }
  };

  const handleTestConnector = async () => {
    try {
      const res = await testCapability.mutateAsync(capability.id);
      if (res.success) {
        toast.success('Connector OK', res.message);
      } else {
        toast.error('Connector Error', res.message);
      }
    } catch (err) {
      toast.error('Test failed', err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const renderParamInput = (param: typeof capability.parameters[0]) => {
    const value = paramValues[param.name];

    if (param.enum && param.enum.length > 0) {
      const options = [
        { value: '', label: 'Select...' },
        ...param.enum.map((opt) => ({ value: opt, label: opt })),
      ];
      return (
        <Select
          value={value as string}
          onChange={(e) => handleParamChange(param.name, e.target.value)}
          options={options}
        />
      );
    }

    switch (param.type) {
      case 'boolean':
        return (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={value as boolean}
              onChange={(e) => handleParamChange(param.name, e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-700"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {value ? 'true' : 'false'}
            </span>
          </div>
        );

      case 'integer':
      case 'number':
        return (
          <Input
            type="number"
            value={value as string}
            onChange={(e) => handleParamChange(param.name, e.target.value)}
            placeholder={`Enter ${param.type}`}
          />
        );

      case 'array':
      case 'object':
        return (
          <Textarea
            value={
              typeof value === 'string'
                ? value
                : JSON.stringify(value, null, 2)
            }
            onChange={(e) => handleParamChange(param.name, e.target.value)}
            placeholder={
              param.type === 'array'
                ? 'Comma-separated values or JSON array'
                : 'JSON object'
            }
            rows={3}
            className="font-mono text-sm"
          />
        );

      default:
        return (
          <Input
            value={value as string}
            onChange={(e) => handleParamChange(param.name, e.target.value)}
            placeholder={param.description || `Enter ${param.name}`}
          />
        );
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Test: ${capability.name}`}
      size="xl"
    >
      <div className="flex h-[70vh]">
        {/* Left Panel - Parameters */}
        <div className="w-1/2 border-r overflow-y-auto p-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Parameters</h3>
              {capability.connector_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestConnector}
                  disabled={testCapability.isPending}
                >
                  {testCapability.isPending ? (
                    <Spinner size="sm" className="mr-1" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-1" />
                  )}
                  Test Connector
                </Button>
              )}
            </div>

            {capability.parameters.length === 0 ? (
              <Card className="p-4 text-center text-gray-500 text-sm dark:text-gray-400">
                This capability has no parameters
              </Card>
            ) : (
              <div className="space-y-4">
                {capability.parameters.map((param) => (
                  <div key={param.name}>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                      {param.name}
                      {param.required && (
                        <Badge variant="error" className="text-xs">Required</Badge>
                      )}
                      <Badge variant="default" className="text-xs">{param.type}</Badge>
                    </label>
                    {renderParamInput(param)}
                    {param.description && (
                      <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                        {param.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="pt-4 border-t">
              <Button
                className="w-full"
                onClick={handleExecute}
                disabled={executeCapability.isPending}
              >
                {executeCapability.isPending ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Executing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Execute
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Right Panel - Results & Schemas */}
        <div className="w-1/2 overflow-y-auto">
          <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
            <TabList>
              <Tab id="result">Result</Tab>
              <Tab id="mcp">MCP Schema</Tab>
              <Tab id="openai">OpenAI Schema</Tab>
            </TabList>

            <TabPanel id="result">
              <div className="p-4">
                {result ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      {result.success ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      )}
                      <span
                        className={`font-medium ${
                          result.success ? 'text-green-700' : 'text-red-700'
                        }`}
                      >
                        {result.success ? 'Success' : 'Failed'}
                      </span>
                      {result.execution_time_ms && (
                        <span className="text-sm text-gray-500 flex items-center gap-1 dark:text-gray-400">
                          <Clock className="h-4 w-4" />
                          {result.execution_time_ms.toFixed(2)}ms
                        </span>
                      )}
                    </div>

                    {result.error && (
                      <Alert type="error">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          <div>
                            {result.error_code && (
                              <Badge variant="error" className="mr-2">
                                {result.error_code}
                              </Badge>
                            )}
                            {result.error}
                          </div>
                        </div>
                      </Alert>
                    )}

                    {result.data && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            Response Data
                          </h4>
                          <button
                            onClick={() =>
                              copyToClipboard(JSON.stringify(result.data, null, 2))
                            }
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-12 dark:text-gray-400">
                    <Play className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Execute the capability to see results</p>
                  </div>
                )}
              </div>
            </TabPanel>

            <TabPanel id="mcp">
              <div className="p-4">
                {mcpLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="lg" />
                  </div>
                ) : mcpSchema ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        MCP Tool Schema
                      </h4>
                      <button
                        onClick={() =>
                          copyToClipboard(JSON.stringify(mcpSchema, null, 2))
                        }
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                      {JSON.stringify(mcpSchema, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <Alert type="warning">
                    Failed to load MCP schema
                  </Alert>
                )}
              </div>
            </TabPanel>

            <TabPanel id="openai">
              <div className="p-4">
                {openaiLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="lg" />
                  </div>
                ) : openaiSchema ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        OpenAI Function Schema
                      </h4>
                      <button
                        onClick={() =>
                          copyToClipboard(JSON.stringify(openaiSchema, null, 2))
                        }
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                      {JSON.stringify(openaiSchema, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <Alert type="warning">
                    Failed to load OpenAI schema
                  </Alert>
                )}
              </div>
            </TabPanel>
          </Tabs>
        </div>
      </div>
    </Modal>
  );
}
